import React, { Fragment, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import SectionItem from '@components/Layout/SectionItem/SectionItem'
import './Survey.scss'
import { useIntl } from 'react-intl'
import SectionItemContainer from '@/app/components/Layout/SectionItemContainer/SectionItemContainer'
import AddAttachment from '@components/UI/AddAttachment/AddAttachment'
import {
    LuxButton,
    LuxTextInput,
    LuxRichInputText,
    LuxInputSwitch,
    LuxDataTable,
    LuxIconSpinner,
    Color,
} from '@lux/wardrobe-lib'
import Duration from '../Duration/Duration'
import { columns } from './table-config'
import SurveySearch from './SurveySearch/SurveySearch'
import { FiltersService } from '@/app/services/FiltersService'
import { ProjectsService } from '@/app/services/ProjectsService'
import { FilterMatchMode } from 'primereact/api'
import { NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE } from '@/app/redux/reducers/activity-creation'
import { downloadSurveyList } from '@/app/redux/actions/activity-execution'

const Survey = (props) => {
    const {
        form,
        onSave,
        onReduce,
        disabled,
        bannerOptions,
        countryOptions,
        regionOptions,
        downloadSurveyList,
    } = props

    const intl = useIntl()

    const componentClass = 'smart-new-activity-action-survey'

    const [banner, setBanner] = useState<string[]>(bannerOptions)
    const [country, setCountry] = useState<string[]>(countryOptions)
    const [region, setRegion] = useState<string[]>(regionOptions)
    const [loading, setLoading] = useState<boolean>(false)

    const [surveyList, setSurveyList] = useState([])

    const [filters, setFilters] = useState({
        surveyName: {
            value: '',
            matchMode: FilterMatchMode.CONTAINS,
            onChange: (v) => onChangeFilter(v, 'surveyName'),
        },
        surveyType: {
            value: '',
            matchMode: FilterMatchMode.CONTAINS,
            onChange: (v) => onChangeFilter(v, 'surveyType'),
        },
    })

    const onChangeFilter = (value, field) => {
        setFilters({
            ...filters,
            [field]: { ...filters[field], value: value },
        })
    }

    useEffect(() => {
        setLoading(true)
        let _banner = banner
        let _region = region
        let _country = region
        if (banner.length == 0 && region.length == 0 && country.length == 0) {
            FiltersService.GetStoreList({ mode: 'simple', coneOfVision: true })
                .then((res: any) => {
                    if (res && res.data) {
                        const reduce = res.data.coneOfVision.reduce(
                            (acc, current) => ({
                                ...acc,
                                banner: [...acc.banner, current.banner],
                                region: [...acc.region, current.macroregion],
                                country: [...acc.country, current.country],
                            }),
                            { banner: [], region: [], country: [] }
                        )
                        _banner = reduce.banner
                        _region = reduce.region
                        _country = reduce.country
                    }
                    ProjectsService.GetSurveyList(_banner, _region, _country)
                        .then((res: any) => {
                            if (res && res.data && res.data.surveys) {
                                setSurveyList(
                                    res.data.surveys.map((survey: any) => ({
                                        ...survey,
                                        disabled: false,
                                    }))
                                )
                                setLoading(false)
                            } else {
                                setSurveyList([])
                                setLoading(false)
                            }
                        })
                        .catch((error: any) => {
                            setSurveyList([])
                            setLoading(false)
                        })
                })
                .catch((error: any) => {
                    setSurveyList([])
                    setLoading(false)
                })
        }
        if (banner.length > 0 || region.length > 0 || country.length > 0) {
            ProjectsService.GetSurveyList(banner, region, country)
                .then((res: any) => {
                    if (res && res.data && res.data.surveys) {
                        setSurveyList(
                            res.data.surveys.map((survey: any) => ({
                                ...survey,
                                disabled: false,
                            }))
                        )
                        setLoading(false)
                    } else {
                        setSurveyList([])
                        setLoading(false)
                    }
                })
                .catch((error: any) => {
                    setSurveyList([])
                    setLoading(false)
                })
        }
    }, [banner, region, country])

    const onSaveHandler = () => {
        onSave({ isDone: true, isEdit: false })
    }

    const onUpload = (files) => {
        onSave({
            attachmentList: [
                ...form.attachmentList,
                ...files.map((file: any) => ({
                    file: file.file,
                    url: file.url,
                    id: file.id,
                })),
            ],
        })
    }
    const onRemoveAttachment = (file) => {
        let newList = [...form.attachmentList]
        const fileIndex = newList.findIndex((i) => i.file.name === file.name)
        newList.splice(fileIndex, 1)
        onSave({
            attachmentList: [...newList],
        })
    }

    const onSelectedRows = (value) => {
        onSave({ selectedSurveyList: value })
    }

    const onClear = () => {
        if (!disabled) {
            setBanner(bannerOptions)
            setCountry(countryOptions)
            setRegion(regionOptions)
            onSave({
                title: '',
                description: '',
                urgent: false,
                attachmentList: [],
                questionList: [],
                startDate: null,
                endDate: null,
                recipient: null,
                selectedSurveyList: null,
            })
        }
    }

    const isSurveyDone = () => {
        return (
            !!form.title &&
            !!form.description &&
            !!form.startDate &&
            !!form.endDate &&
            !!form.recipient &&
            !!form.selectedSurveyList
        )
    }

    const ctaTemplate = (
        <Fragment>
            <div className="link-mini" onClick={onClear}>
                {intl.formatMessage({
                    id: 'modals.new.activity.section.clear',
                    defaultMessage: 'clear',
                })}
            </div>
            <LuxButton
                onClick={onSaveHandler}
                disabled={disabled || !isSurveyDone()}
                text={intl.formatMessage({
                    id: 'modals.new.activity.section.action.survey.save',
                    defaultMessage: 'Save task',
                })}
                variant="outlined"
            />
        </Fragment>
    )

    return (
        <>
            {loading ? (
                <LuxIconSpinner />
            ) : (
                <SectionItemContainer ctaTemplate={ctaTemplate}>
                    <SectionItem>
                        <div className={`${componentClass}__item-title`} onClick={() => onReduce()}>
                            <span className={`${componentClass}__item-title-reduce`}>-</span>
                            <span className="link-mini">{props.title}</span>
                        </div>
                    </SectionItem>
                    <SectionItem>
                        <div className={`${componentClass}__type`}>
                            <LuxTextInput
                                value={form.title}
                                disabled={disabled}
                                mandatory={true}
                                onChangeCallback={(value) => onSave({ title: value })}
                                label={intl.formatMessage({
                                    id: 'modals.new.activity.section.action.survey.input.title',
                                    defaultMessage: 'Survey title',
                                })}
                                placeholder={intl.formatMessage({
                                    id: 'modals.new.activity.section.action.survey.input.title.placeholder',
                                    defaultMessage: 'Type here...',
                                })}
                                style={{ backgroundColor: Color.WHITE }}
                            />
                        </div>
                    </SectionItem>
                    <div className={`${componentClass}__field-container`}>
                        <LuxRichInputText
                            value={form.description}
                            mandatory={true}
                            onChangeCallback={(value) => onSave({ description: value })}
                            label={intl.formatMessage({
                                id: 'modals.new.activity.section.action.survey.description',
                                defaultMessage: 'Survey description',
                            })}
                            placeholder={intl.formatMessage({
                                id: 'modals.new.activity.section.action.survey.description.placeholder',
                                defaultMessage: 'Type here',
                            })}
                            config={{
                                events: {
                                    initialized() {
                                        const editor = this
                                        if (disabled) editor.edit.off()
                                    },
                                },
                            }}
                            fileUploadUrl={ProjectsService.FROALA_UPLOAD_URL}
                        />
                    </div>
                    <SectionItem>
                        <AddAttachment
                            id={`survey-${Math.random() * 100}`}
                            showSplitFile={false}
                            form={{ attachmentList: { value: form.attachmentList } }}
                            onUpload={(form) => onUpload(form)}
                            showRemoveIcon
                            onRemove={(file) => onRemoveAttachment(file)}
                            disabled={disabled}
                        />
                    </SectionItem>
                    <SectionItem>
                        <Duration
                            form={{
                                startDate: form.startDate,
                                endDate: form.endDate,
                                recipient: form.recipient,
                            }}
                            onSave={(form) => onSave(form)}
                            disabled={disabled}
                            pickerClass="survey-picker"
                        />
                    </SectionItem>
                    <SectionItem>
                        <LuxInputSwitch
                            value={form.urgent}
                            onChangeCallback={() => onSave({ urgent: !form.urgent })}
                            label={intl.formatMessage({
                                id: 'modals.new.activity.section.action.task.urgent',
                                defaultMessage: 'Urgent',
                            })}
                            additionalProps={{ disabled }}
                        />
                    </SectionItem>
                    <SectionItem>
                        <SurveySearch
                            form={{ banner, country, region }}
                            onSetBanner={setBanner}
                            onSetCountry={setCountry}
                            onSetRegion={setRegion}
                            disabled={disabled}
                        />
                    </SectionItem>
                    <SectionItem>
                        <LuxDataTable
                            label={intl.formatMessage({
                                id: 'modals.new.activity.section.action.survey.list',
                                defaultMessage: 'Store list',
                            })}
                            mandatory={true}
                            selectionMode={'single'}
                            columns={columns.map((column) => ({
                                ...column,
                                header: column.header
                                    ? intl.formatMessage({
                                          id: column.header,
                                          defaultMessage: column.header.split('.').pop(),
                                      })
                                    : '',
                            }))}
                            rowsPerPageOptions={[5, 10, 15]}
                            dataSet={surveyList}
                            scrollable
                            selectedRows={form.selectedSurveyList}
                            setSelectedRows={(value) => onSelectedRows(value)}
                            actionsList={[
                                {
                                    icon: 'caret-right',
                                    command: (e) =>
                                        window.open(`${e.surveyUrl}?readOnly=true`, '_blank'),
                                },
                            ]}
                            dataKey="surveyId"
                            columnFilters={filters}
                            labels={{
                                filter: intl.formatMessage({
                                    id: 'modals.new_resource.distribution.store_list.filter',
                                    defaultMessage: 'filter',
                                }),
                                rearrange: intl.formatMessage({
                                    id: 'modals.new_resource.distribution.store_list.rearrange',
                                    defaultMessage: 'rearrange',
                                }),
                                of: intl.formatMessage({
                                    id: 'modals.new_resource.distribution.store_list.labels.of',
                                    defaultMessage: 'of',
                                }),
                                selected: intl.formatMessage({
                                    id: 'modals.new_resource.distribution.store_list.labels.selected',
                                    defaultMessage: 'selected',
                                }),
                                emptyMessage: intl.formatMessage({
                                    id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                                    defaultMessage: 'No results to show.',
                                }),
                            }}
                            onDownload={() => {
                                downloadSurveyList(banner, country)
                            }}
                        />
                    </SectionItem>
                </SectionItemContainer>
            )}
        </>
    )
}

const mapStateToProps = (state: any) => ({
    bannerOptions: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].banner,
    regionOptions: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].region,
    countryOptions: state[NEW_ACTIVITY_CREATION_REDUCER_NAMESPACE].country,
})

const mapDispatchToProps = (dispatch: any) => ({
    downloadSurveyList: (banner, country) =>
        dispatch(downloadSurveyList(banner, country, 'SURVEY')),
})

export default connect(mapStateToProps, mapDispatchToProps)(Survey)
